/**
 * Bulk Actions Modal Component - Detachable Admin Module
 * 
 * This component provides bulk action modals for credit and plan management across multiple users.
 */

import React, { useState, useEffect } from 'react';
import { Button, Input, Select } from '../../components';
import { Icon, Text } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { useAdminData } from '../hooks/useAdminData';
import { getUserDisplayName } from '../utils';
import type { CreditType, PlanType, PlatformUser } from '../types';

interface BulkActionsModalProps {
  users: PlatformUser[];
  action: 'addCredits' | 'changePlan' | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface OperationResult {
  userId: string;
  userName: string;
  success: boolean;
  error?: string;
}

/**
 * Modal component for bulk credit and plan management actions
 */
export const BulkActionsModal: React.FC<BulkActionsModalProps> = ({
  users,
  action,
  onClose,
  onSuccess
}) => {
  const { operations } = useAdminData();
  const [isClosing, setIsClosing] = useState(false);

  // Form state
  const [creditForm, setCreditForm] = useState({
    amount: '',
    creditType: 'free_platform' as CreditType,
    description: ''
  });

  const [planForm, setPlanForm] = useState({
    planType: 'starter' as PlanType,
    monthlyLimit: ''
  });

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<OperationResult[]>([]);
  const [currentProgress, setCurrentProgress] = useState(0);

  // Reset state when modal opens or action changes
  useEffect(() => {
    if (action) {
      setResults([]);
      setCurrentProgress(0);
      operations.clearError();
      setCreditForm({ amount: '', creditType: 'free_platform' as CreditType, description: '' });
      setPlanForm({ planType: 'starter' as PlanType, monthlyLimit: '' });
    }
  }, [action]);

  const handleBulkAddCredits = async () => {
    const amount = parseFloat(creditForm.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid credit amount');
      return;
    }

    setProcessing(true);
    const operationResults: OperationResult[] = [];

    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      setCurrentProgress(Math.round(((i + 1) / users.length) * 100));

      try {
        const success = await operations.addCredits(
          user.id || user.email,
          amount,
          creditForm.creditType,
          creditForm.description || `Credit Grant`
        );

        operationResults.push({
          userId: user.id || user.email,
          userName: getUserDisplayName(user),
          success
        });
      } catch (error: any) {
        operationResults.push({
          userId: user.id || user.email,
          userName: getUserDisplayName(user),
          success: false,
          error: error.message || 'Operation failed'
        });
      }
    }

    setResults(operationResults);
    setProcessing(false);

    const allSuccess = operationResults.every(r => r.success);
    if (allSuccess) {
      setTimeout(() => {
        setResults([]);
        setCurrentProgress(0);
        onSuccess();
        handleClose();
      }, 1500);
    }
  };

  const handleBulkChangePlan = async () => {
    setProcessing(true);
    const operationResults: OperationResult[] = [];

    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      setCurrentProgress(Math.round(((i + 1) / users.length) * 100));

      try {
        const monthlyLimit = planForm.monthlyLimit ? parseFloat(planForm.monthlyLimit) : undefined;

        const success = await operations.changePlan(
          user.id || user.email,
          planForm.planType,
          undefined,
          monthlyLimit
        );

        operationResults.push({
          userId: user.id || user.email,
          userName: getUserDisplayName(user),
          success
        });
      } catch (error: any) {
        operationResults.push({
          userId: user.id || user.email,
          userName: getUserDisplayName(user),
          success: false,
          error: error.message || 'Operation failed'
        });
      }
    }

    setResults(operationResults);
    setProcessing(false);

    const allSuccess = operationResults.every(r => r.success);
    if (allSuccess) {
      setTimeout(() => {
        setResults([]);
        setCurrentProgress(0);
        onSuccess();
        handleClose();
      }, 1500);
    }
  };

  const handleClose = () => {
    if (!processing) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        operations.clearError();
        setResults([]);
        setCurrentProgress(0);
        onClose();
      }, 300);
    }
  };

  const panelIsOpen = (!!action && users.length > 0) || results.length > 0;

  if (!panelIsOpen && !isClosing) return null;

  const isResultsView = results.length > 0;
  const successCount = results.filter(r => r.success).length;
  const failureCount = results.filter(r => !r.success).length;

  const getTitle = () => {
    if (isResultsView) return 'Bulk Operation Results';
    if (action === 'addCredits') return `Bulk Add Credits (${users.length} users)`;
    return `Bulk Change Plan (${users.length} users)`;
  };

  const creditTypeOptions = [
    { value: 'free_platform', label: 'Free Platform Credits' },
    { value: 'monthly', label: 'Monthly Credits' },
    { value: 'paid_platform', label: 'Paid Platform Credits' },
  ];

  const planTypeOptions = [
    { value: 'starter', label: 'Starter Plan' },
    { value: 'monthly', label: 'Monthly Plan' },
    { value: 'unlimited', label: 'Unlimited Plan' },
  ];

  const userList = (
    <div className="bg-bg-brand-primary p-bg-v2-lg rounded-bg-v2-md">
      <p className="text-sm text-text-brand-secondary mb-bg-v2-md font-medium">Selected Users:</p>
      <div className="max-h-32 overflow-y-auto space-y-bg-v2-xs">
        {users.map(user => (
          <p key={user.id || user.email} className="text-xs text-text-brand-secondary">
            • {getUserDisplayName(user)} ({user.email})
          </p>
        ))}
      </div>
    </div>
  );

  const progressBar = processing && (
    <div className="bg-bg-brand-primary p-bg-v2-lg rounded-bg-v2-md">
      <div className="flex items-center justify-between mb-bg-v2-md">
        <span className="text-sm text-text-brand-secondary">Processing...</span>
        <span className="text-sm font-medium text-text-brand-primary">{currentProgress}%</span>
      </div>
      <div className="w-full bg-bg-brand-secondary rounded-bg-v2-full h-2">
        <div
          className="bg-bg-brand-solid h-2 rounded-bg-v2-full transition-all duration-300"
          style={{ width: `${currentProgress}%` }}
        />
      </div>
    </div>
  );

  const errorMessage = operations.error && (
    <div className="text-sm text-text-error-primary bg-bg-error-primary p-bg-v2-md rounded-bg-v2-sm">
      {operations.error}
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-alpha-black-70 z-[200] ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
      />
      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-screen z-[200] bg-bg-primary w-[420px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        style={{
          boxShadow: '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
        }}
      >
        {/* Header */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <Text variant="text-xl" weight="bold">{getTitle()}</Text>
          <button
            onClick={handleClose}
            disabled={processing}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">

          {/* Results View */}
          {isResultsView && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-success-primary font-medium">✓ {successCount} Successful</span>
                {failureCount > 0 && (
                  <span className="text-text-error-primary font-medium">✗ {failureCount} Failed</span>
                )}
              </div>
              <div className="space-y-bg-v2-md">
                {results.map((result) => (
                  <div
                    key={result.userId}
                    className={`p-bg-v2-lg rounded-bg-v2-md flex items-center space-x-bg-v2-lg ${
                      result.success
                        ? 'bg-bg-success-primary border border-border-secondary'
                        : 'bg-bg-error-primary border border-border-error-subtle'
                    }`}
                  >
                    <span className={`text-lg ${result.success ? 'text-text-success-primary' : 'text-text-error-primary'}`}>
                      {result.success ? '✓' : '✗'}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-text-primary">{result.userName}</div>
                      {result.error && (
                        <div className="text-xs text-text-error-primary">{result.error}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Add Credits Form */}
          {!isResultsView && action === 'addCredits' && (
            <>
              {userList}
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max="10000"
                value={creditForm.amount}
                onChange={(e) => setCreditForm(prev => ({ ...prev, amount: e.target.value }))}
                label="Credit Amount ($)"
                placeholder="0.00"
                required
                autoFocus
                disabled={processing}
                helperText="This amount will be added to each selected user"
              />
              <Select
                label="Credit Type"
                options={creditTypeOptions}
                value={creditForm.creditType}
                onChange={(value) => setCreditForm(prev => ({ ...prev, creditType: value as CreditType }))}
                disabled={processing}
              />
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-bg-v2-xs">
                  Description (Optional)
                </label>
                <textarea
                  value={creditForm.description}
                  onChange={(e) => setCreditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="block w-full px-bg-v2-lg py-bg-v2-md border border-border-primary rounded-bg-v2-md focus:ring-bg-brand-solid focus:border-border-brand-alt bg-bg-primary text-text-primary placeholder:text-text-placeholder"
                  rows={3}
                  placeholder="Reason for bulk credit grant..."
                  disabled={processing}
                />
              </div>
              {progressBar}
              {errorMessage}
            </>
          )}

          {/* Change Plan Form */}
          {!isResultsView && action === 'changePlan' && (
            <>
              {userList}
              <Select
                label="New Plan Type"
                options={planTypeOptions}
                value={planForm.planType}
                onChange={(value) => setPlanForm(prev => ({ ...prev, planType: value as PlanType }))}
                disabled={processing}
                required
              />
              {planForm.planType === 'monthly' && (
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={planForm.monthlyLimit}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, monthlyLimit: e.target.value }))}
                  label="Monthly Credit Limit ($)"
                  placeholder="Leave empty to use default"
                  disabled={processing}
                />
              )}
              {progressBar}
              {errorMessage}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
          {isResultsView ? (
            <Button
              variant="primary"
              label={failureCount > 0 ? 'Close' : 'Done'}
              onClick={handleClose}
            />
          ) : (
            <>
              <Button
                variant="outline"
                label="Cancel"
                onClick={handleClose}
                disabled={processing}
              />
              {action === 'addCredits' && (
                <Button
                  variant="success"
                  label={processing ? 'Processing...' : 'Add Credits to All'}
                  onClick={handleBulkAddCredits}
                  disabled={processing}
                />
              )}
              {action === 'changePlan' && (
                <Button
                  label={processing ? 'Processing...' : 'Change Plan for All'}
                  onClick={handleBulkChangePlan}
                  disabled={processing}
                  className="bg-utility-purple-600 hover:bg-utility-purple-700"
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BulkActionsModal;
